import { useEffect, useRef, useState } from "react";

/**
 * Lightweight touch-swipe hook for mobile gestures.
 *
 * Attach the returned `bind` props to any element. Distinguishes horizontal
 * vs vertical intent by comparing |dx| / |dy| ratio and a minimum distance
 * threshold so accidental diagonal taps don't trigger.
 *
 * Auto-disabled when `enabled === false` (use `useIsMobile()` from this
 * module to gate by breakpoint).
 */
const DEFAULT_THRESHOLD = 50;
const RATIO_THRESHOLD = 1.3;

export function useSwipe({
  enabled = true,
  threshold = DEFAULT_THRESHOLD,
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
} = {}) {
  const stateRef = useRef({ x: 0, y: 0, active: false });

  const onTouchStart = (e) => {
    if (!enabled) return;
    const t = e.touches[0];
    stateRef.current = { x: t.clientX, y: t.clientY, active: true };
  };

  const onTouchEnd = (e) => {
    if (!enabled || !stateRef.current.active) return;
    stateRef.current.active = false;
    const t = e.changedTouches[0];
    const dx = t.clientX - stateRef.current.x;
    const dy = t.clientY - stateRef.current.y;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);
    if (adx < threshold && ady < threshold) return;
    if (adx >= ady * RATIO_THRESHOLD) {
      if (dx < 0) onSwipeLeft?.();
      else onSwipeRight?.();
    } else if (ady >= adx * RATIO_THRESHOLD) {
      if (dy < 0) onSwipeUp?.();
      else onSwipeDown?.();
    }
  };

  return { bind: { onTouchStart, onTouchEnd } };
}

/**
 * `useIsMobile()` — true when viewport ≤ breakpoint px. Re-renders on resize.
 */
export function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}
